/**
 * usePaypalFeeEstimate — dérive, pour l'estimation « couvrir les frais », le
 * provider effectif du paiement (Stripe vs PayPal) et le tarif PayPal appris
 * (percentage + fixed) exposé dans `payment_providers.paypal`.
 *
 * Utilisé par toutes les surfaces de don/commande pour que la couverture des
 * frais soit calculée AU BON TAUX quand le donateur paiera en PayPal. Si PayPal
 * n'est pas le moyen effectif → l'estimation reste au tarif Stripe (inchangé).
 *
 * Le choix du provider effectif MIROIR de `usesPaypalCard` (DonateCheckoutApp) :
 *  - PayPal seul → paypal
 *  - récurrent + Stripe dispo → stripe (le récurrent passe par Stripe en primaire)
 *  - les 2 dispos + préférence org = paypal → paypal
 *  - sinon → stripe
 */
/**
 * Type structurel MINIMAL de ce que le helper lit. Accepte aussi bien
 * `OrgPaymentProviders` (strict) que les variantes à champs optionnels utilisées
 * par certaines pages (a/, b/) → évite un cast au site d'appel.
 */
interface PaymentProvidersLike {
  stripe?: { charges_enabled?: boolean } | undefined;
  paypal?:
    | { charges_enabled?: boolean; fee_percentage?: number; fee_fixed?: number }
    | undefined;
  preferred_provider?: "stripe" | "paypal";
}

export function usePaypalFeeEstimate(
  paymentProviders: Ref<PaymentProvidersLike>,
  isSubscription: Ref<boolean>,
) {
  const estimateProvider = computed<"stripe" | "paypal">(() => {
    const pp = paymentProviders.value;
    const stripeOn = pp.stripe?.charges_enabled === true;
    const paypalOn = pp.paypal?.charges_enabled === true;
    if (paypalOn && !stripeOn) return "paypal";
    if (isSubscription.value && stripeOn) return "stripe";
    if (stripeOn && paypalOn && pp.preferred_provider === "paypal") return "paypal";
    return "stripe";
  });

  // Défauts asso si l'API/org n'expose pas encore le tarif appris.
  const paypalFeePercent = computed<number>(
    () => paymentProviders.value.paypal?.fee_percentage ?? 1.4,
  );
  const paypalFeeFixed = computed<number>(
    () => paymentProviders.value.paypal?.fee_fixed ?? 0.25,
  );

  return { estimateProvider, paypalFeePercent, paypalFeeFixed };
}
